import { api, type KotatsuImportSummary, type AnitubeStatus } from '../api';
import { getCurrentRole, requireAuthOrRedirect } from '../auth';
import { canAccess } from '../roles';
import { confirmDialog, toast } from '../utils';

export async function initSettings() {
  const username = await requireAuthOrRedirect();
  if (!username) return;

  // Гостьовий акаунт (tier_1_subscriber) має підмножину можливостей: у "Відновленні" лише імпорт/експорт
  // бекапів цього сайту, без імпорту з Kotatsu/Mihon, без anitube. Це лише інтерфейс — сервер так само
  // віддає 403 на ці ендпоінти (src/lib/roles.ts).
  const role = await getCurrentRole();
  const foreignImport = canAccess(role, 'restore.foreign-import');
  const anitubeOk = canAccess(role, 'anitube');
  // Ці блоки приховані ще в HTML (клас gated) — інакше гість на мить бачив би їх до виконання цього коду.
  const foreignIds = ['import-kotatsu-btn', 'import-kotatsu-panel', 'import-kotatsu-file', 'import-mihon-btn', 'import-mihon-panel', 'import-mihon-file'];
  const anitubeIds = ['anitube-divider', 'anitube-section-toggle', 'anitube-section-body'];
  for (const [ok, ids] of [[foreignImport, foreignIds], [anitubeOk, anitubeIds]] as const) {
    for (const id of ids) {
      const el = document.getElementById(id);
      if (ok) el?.classList.remove('gated');
      else el?.remove();
    }
  }

  setupDropdown('data-section-toggle', 'data-section-body', 'data-section-chevron');
  setupDropdown('backup-section-toggle', 'backup-section-body', 'backup-section-chevron');
  if (anitubeOk) setupDropdown('anitube-section-toggle', 'anitube-section-body', 'anitube-section-chevron');

  const clearHistoryBtn = document.getElementById('clear-history') as HTMLElement;
  clearHistoryBtn.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: 'Очистити історію читання?',
      message: 'Список переглянутого буде видалено. Статус прочитаних розділів не зміниться.',
      confirmText: 'Очистити',
      danger: true,
    });
    if (!ok) return;
    await api.clearHistory();
    toast('Історію очищено');
  });

  const clearLibraryBtn = document.getElementById('clear-library') as HTMLElement;
  clearLibraryBtn.addEventListener('click', async () => {
    const ok = await confirmDialog({
      title: 'Очистити бібліотеку?',
      message: 'Усі категорії й додані твори буде видалено. Історія читання не постраждає.',
      confirmText: 'Очистити',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.clearLibrary();
      toast('Бібліотеку очищено');
    } catch (err) {
      toast((err as Error).message);
    }
  });

  const forgetBtn = document.getElementById('forget-everything') as HTMLElement;
  forgetBtn.addEventListener('click', async () => {
    const confirmed = await openForgetEverythingDialog();
    if (!confirmed) return;
    try {
      await api.forgetEverything();
    } catch (err) {
      toast((err as Error).message);
      return;
    }
    location.href = '/more';
  });

  setupBackupExportImport();

  if (foreignImport) {
    setupBackupImport({
    endpoint: 'kotatsu',
    idPrefix: 'import-kotatsu',
    fullLabel: 'Імпортую бекап… Твори з mangainua довантажуються по одному з паузами (щоб не впіймати 429), тож це може зайняти кілька хвилин.',
    skippedLabel: 'Пропущено (інші джерела Kotatsu, яких немає в застосунку)',
  });
  setupBackupImport({
    endpoint: 'mihon',
    idPrefix: 'import-mihon',
    fullLabel: 'Імпортую бекап… Джерела з обмеженнями довантажуються з паузами (щоб не впіймати 429), тож це може зайняти кілька хвилин.',
    skippedLabel: 'Пропущено (інші джерела Mihon, для яких немає плагіна)',
  });
  }

  if (anitubeOk) setupAnitube();

  if (username === 'admin') setupAdminLog();
}

/**
 * "Лог сервера" (лише admin) — жива трансляція console.log/warn/error
 * сервера через SSE (src/routes/logs.ts). Секція в HTML прихована за
 * замовчуванням (style="display:none") — показуємо її лише тут, ПІСЛЯ
 * підтвердження, що username === 'admin', щоб не з'являлась навіть на
 * мить іншим користувачам.
 */
function setupAdminLog() {
  const divider = document.getElementById('admin-log-divider') as HTMLElement;
  const section = document.getElementById('admin-log-section') as HTMLElement;
  divider.style.display = '';
  section.style.display = '';
  setupDropdown('log-section-toggle', 'log-section-body', 'log-section-chevron');

  const box = document.getElementById('server-log-box') as HTMLElement;
  const dot = document.getElementById('server-log-dot') as HTMLElement;
  const statusText = document.getElementById('server-log-status-text') as HTMLElement;
  const clearBtn = document.getElementById('server-log-clear') as HTMLButtonElement;
  const pauseBtn = document.getElementById('server-log-pause') as HTMLButtonElement;

  const MAX_LINES = 1000;
  let paused = false;

  function fmtTime(ms: number): string {
    return new Date(ms).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  function appendLine(entry: { time: number; level: string; text: string }) {
    if (paused) return;
    const wasAtBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 4;
    const line = document.createElement('div');
    line.className = `server-log-line level-${entry.level}`;
    line.textContent = `[${fmtTime(entry.time)}] ${entry.text}`;
    box.appendChild(line);
    while (box.children.length > MAX_LINES) box.removeChild(box.firstChild as ChildNode);
    if (wasAtBottom) box.scrollTop = box.scrollHeight;
  }

  clearBtn.addEventListener('click', () => {
    box.innerHTML = '';
  });

  pauseBtn.addEventListener('click', () => {
    paused = !paused;
    pauseBtn.textContent = paused ? 'Відновити' : 'Призупинити';
  });

  // EventSource сам перепідключається при обриві (браузерна поведінка за
  // замовчуванням) — окремої логіки ретраю тут не потрібно.
  const source = new EventSource('/api/logs/stream');
  source.addEventListener('open', () => {
    dot.classList.remove('offline');
    statusText.textContent = 'Наживо';
  });
  source.addEventListener('error', () => {
    dot.classList.add('offline');
    statusText.textContent = "З'єднання перервано, перепідключаюсь…";
  });
  source.onmessage = (ev) => {
    try {
      appendLine(JSON.parse(ev.data));
    } catch {
      /* бита подія — ігноруємо */
    }
  };
}

/** Спільна логіка розкривного списку (Дані / Резервне копіювання / Синхронізація з Anitube) — за замовчуванням згорнутий. */
export function setupDropdown(toggleId: string, bodyId: string, chevronId: string) {
  const toggle = document.getElementById(toggleId) as HTMLElement;
  const body = document.getElementById(bodyId) as HTMLElement;
  const chevron = document.getElementById(chevronId) as HTMLElement;
  const toggleFn = () => {
    const isOpen = body.style.display !== 'none';
    body.style.display = isOpen ? 'none' : 'block';
    chevron.classList.toggle('open', !isOpen);
  };
  toggle.addEventListener('click', toggleFn);
  toggle.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFn();
    }
  });
}

/**
 * Власна (у стилі застосунку) модалка для "Забути усе": опис наслідків
 * виводиться над полем підтвердження, підтвердити можна лише ввівши
 * слово "забути".
 */
function openForgetEverythingDialog(): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay-center';
    overlay.innerHTML = `
      <div class="center-modal">
        <h3 class="modal-title">Забути усе?</h3>
        <p class="modal-details">Бібліотека, історія, категорії, «Нове» і всі налаштування будуть видалені без можливості відновлення, і вас вийде з акаунта. Сам акаунт (логін/пароль) залишиться.</p>
        <label class="field-block">
          <span class="field-block-label">Введіть слово «забути», щоб підтвердити</span>
          <input type="text" id="forget-confirm-input" class="field-block-input" autocomplete="off" autocapitalize="off" spellcheck="false">
        </label>
        <div class="modal-actions">
          <button type="button" id="forget-confirm-cancel" class="btn">Скасувати</button>
          <button type="button" id="forget-confirm-ok" class="btn btn-danger" disabled>Забути все</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const input = overlay.querySelector('#forget-confirm-input') as HTMLInputElement;
    const okBtn = overlay.querySelector('#forget-confirm-ok') as HTMLButtonElement;
    const cancelBtn = overlay.querySelector('#forget-confirm-cancel') as HTMLButtonElement;

    const isValid = () => input.value.trim().toLowerCase() === 'забути';
    input.addEventListener('input', () => {
      okBtn.disabled = !isValid();
    });

    const close = (result: boolean) => {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter' && isValid()) close(true);
    };
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close(false);
    });
    cancelBtn.addEventListener('click', () => close(false));
    okBtn.addEventListener('click', () => {
      if (isValid()) close(true);
    });
    input.focus();
  });
}

/**
 * "Резервне копіювання та відновлення" → "Створити"/"Відновити" з бекапу
 * ЦЬОГО сайту: експорт — звичайне посилання-завантаження, без JS
 * (Content-Disposition виставляє сервер, routes/library.ts GET
 * /export/backup). Імпорт — вибір файлу через приховану input[type=file],
 * підтвердження (це ПОВНА ЗАМІНА персональних даних, а не домердж, на
 * відміну від імпорту Kotatsu/Mihon нижче, який додає до наявної
 * бібліотеки), і виклик api.importAppBackup.
 */
function setupBackupExportImport() {
  const importBtn = document.getElementById('import-backup-btn') as HTMLElement;
  const fileInput = document.getElementById('import-backup-file') as HTMLInputElement;

  importBtn.addEventListener('click', () => fileInput.click());

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (file) void runImport(file);
  });

  async function runImport(file: File) {
    const ok = await confirmDialog({
      title: 'Імпортувати бекап?',
      message:
        'Поточна бібліотека, категорії, історія, «Нове», позначки прочитаного, налаштування джерел і правила редагування будуть ПОВНІСТЮ ЗАМІНЕНІ вмістом файлу. Підписка на push-сповіщення й підключення anitube не постраждають.',
      confirmText: 'Замінити',
      danger: true,
    });
    if (!ok) return;
    try {
      const result = await api.importAppBackup(file);
      toast(`Бекап відновлено: творів у бібліотеці — ${result.library}, категорій — ${result.categories}`);
      location.href = '/settings';
    } catch (err) {
      toast((err as Error).message);
    }
  }
}

interface BackupImportConfig {
  endpoint: 'kotatsu' | 'mihon';
  /** Спільний префікс id-ів усіх елементів панелі в HTML, напр. "import-kotatsu". */
  idPrefix: string;
  /** Підпис прогресу у повному (мережевому) режимі. */
  fullLabel: string;
  /** Підпис для рядка "Пропущено" у підсумку — формат бекапу різний, тому текст різний. */
  skippedLabel: string;
}

/** Спільна логіка панелі імпорту бекапу — використовується і для Kotatsu, і для Mihon (src/routes/library.ts: /import/kotatsu, /import/mihon). */
function setupBackupImport(cfg: BackupImportConfig) {
  const p = cfg.idPrefix;
  const openBtn = document.getElementById(`${p}-btn`) as HTMLElement;
  const panelEl = document.getElementById(`${p}-panel`) as HTMLElement;
  const dropzone = document.getElementById(`${p}-dropzone`) as HTMLElement;
  const fileInput = document.getElementById(`${p}-file`) as HTMLInputElement;
  const progressEl = document.getElementById(`${p}-progress`) as HTMLElement;
  const progressTextEl = document.getElementById(`${p}-progress-text`) as HTMLElement;
  const progressCountEl = document.getElementById(`${p}-progress-count`) as HTMLElement;
  const progressBarEl = document.getElementById(`${p}-progress-bar`) as HTMLElement;
  const resultEl = document.getElementById(`${p}-result`) as HTMLElement;
  const quickToggle = document.getElementById(`${p}-quick`) as HTMLInputElement;

  function setImportProgress(done: number, total: number) {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    progressCountEl.textContent = total > 0 ? `${done}/${total} (${pct}%)` : '0%';
    progressBarEl.style.width = `${pct}%`;
  }

  // Клік по рядку розкриває панель імпорту (значок для перетягування файлу
  // з'являється по центру робочої зони замість негайного відкриття провідника).
  openBtn.addEventListener('click', () => {
    const isOpen = panelEl.style.display !== 'none';
    if (isOpen) {
      panelEl.style.display = 'none';
      return;
    }
    resultEl.style.display = 'none';
    progressEl.style.display = 'none';
    dropzone.style.display = 'flex';
    panelEl.style.display = 'block';
  });

  dropzone.addEventListener('click', () => fileInput.click());
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });

  let dragDepth = 0;
  dropzone.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragDepth++;
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragover', (e) => e.preventDefault());
  dropzone.addEventListener('dragleave', () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) dropzone.classList.remove('dragover');
  });
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dragDepth = 0;
    dropzone.classList.remove('dragover');
    const file = e.dataTransfer?.files?.[0];
    if (file) void runImport(file);
  });

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (file) void runImport(file);
  });

  async function runImport(file: File) {
    // Значок перетягування зникає, на його місці — прогрес імпорту.
    dropzone.style.display = 'none';
    resultEl.style.display = 'none';
    setImportProgress(0, 0);
    const quick = quickToggle.checked;
    progressTextEl.textContent = quick ? 'Швидко додаю твори з бекапу (без запиту до джерела)…' : cfg.fullLabel;
    progressEl.style.display = 'flex';

    try {
      const summary = await api.importBackup(cfg.endpoint, file, setImportProgress, {
        quick,
        onSyncStart: () => {
          // Друга фаза (лише при швидкому імпорті): додавання вже завершене
          // (це видно з "done" самого summary), тепер сервер у ТІЙ САМІЙ
          // відповіді перевіряє реальні дані по всій бібліотеці — тому не
          // показуємо проміжний результат і не ховаємо прогрес-бар, а просто
          // перемикаємо його підпис на другу фазу.
          progressTextEl.textContent = 'Уточнюю кількість розділів і прогрес читання (звіряю з джерелом)…';
          setImportProgress(0, 0);
        },
        onSyncProgress: setImportProgress,
      });
      renderImportResult(summary);
      toast('Імпорт завершено');
    } catch (err) {
      toast((err as Error).message);
      dropzone.style.display = 'flex';
    } finally {
      progressEl.style.display = 'none';
    }
  }

  function renderImportResult(summary: KotatsuImportSummary) {
    const added = summary.items.filter((i) => i.status === 'added').length;
    const updated = summary.items.filter((i) => i.status === 'updated').length;
    const errors = summary.items.filter((i) => i.status === 'error');

    const parts = [
      `Знайдено творів з наших джерел: ${summary.matched}`,
      `Додано: ${added}`,
      `Оновлено (вже були в бібліотеці): ${updated}`,
      `Помилок: ${errors.length}`,
    ];
    if (summary.categoriesCreated.length) {
      parts.push(`Нові категорії: ${summary.categoriesCreated.join(', ')}`);
    }
    if (summary.skippedOtherSource) {
      parts.push(`${cfg.skippedLabel}: ${summary.skippedOtherSource}`);
    }

    resultEl.innerHTML = '';
    const summaryP = document.createElement('p');
    summaryP.className = 'import-result-summary';
    summaryP.innerHTML = parts.map((p) => escapeHtml(p)).join('<br>');
    resultEl.appendChild(summaryP);

    if (errors.length) {
      const errBox = document.createElement('div');
      errBox.className = 'import-result-errors';
      for (const e of errors) {
        const row = document.createElement('div');
        row.className = 'import-result-error-row';
        row.textContent = `${e.title} — ${e.message ?? 'помилка'}`;
        errBox.appendChild(row);
      }
      resultEl.appendChild(errBox);
    }

    resultEl.style.display = 'block';
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * "Налаштування" → "Синхронізація аніме з Anitube": вхід у акаунт
 * anitube.in.ua (той самий DLE-логін, що й у власному застосунку anitube) і
 * подальша синхронізація категорій аніме-бібліотеки (Переглянуто/
 * Заплановано/Переглядаю/Відкладено/Покинуто) з ним. Категорію "Переглядаю"
 * сервер надалі підтримує сам — при кожній звичайній перевірці оновлень
 * (src/routes/updates.ts) — тому тут лише початковий вхід + кнопка ручної
 * повної синхронізації.
 */
function setupAnitube() {
  const connectBtn = document.getElementById('anitube-connect-btn') as HTMLElement;
  const connectLabel = document.getElementById('anitube-connect-label') as HTMLElement;
  const loginPanel = document.getElementById('anitube-login-panel') as HTMLElement;
  const usernameInput = document.getElementById('anitube-username') as HTMLInputElement;
  const passwordInput = document.getElementById('anitube-password') as HTMLInputElement;
  const errorEl = document.getElementById('anitube-login-error') as HTMLElement;
  const loginSubmit = document.getElementById('anitube-login-submit') as HTMLButtonElement;

  const accountPanel = document.getElementById('anitube-account-panel') as HTMLElement;
  const accountSub = document.getElementById('anitube-account-sub') as HTMLElement;
  const syncBtn = document.getElementById('anitube-sync-btn') as HTMLElement;
  const syncLabel = document.getElementById('anitube-sync-label') as HTMLElement;
  const disconnectBtn = document.getElementById('anitube-disconnect-btn') as HTMLElement;

  function fmtSyncTime(iso: string | null | undefined): string {
    if (!iso) return 'ще не було';
    return new Date(iso).toLocaleString('uk-UA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  function fmtSyncResult(result: {
    changed: number;
    imported: number;
    removed: number;
    segments: { segment: string; categoryName: string; entries: number; matched: number }[];
    unmatchedSample: { name: string; path: string; animeId: number | null }[];
  }): string {
    // Діагностику також друкуємо в консоль браузера — щоб бачити, скільки
    // карток фактично знайдено на кожному з 5 списків anitube і скільки з
    // них вдалось зіставити з конкретним твором (news_id), не лізучи в
    // консоль сервера.
    console.table(result.segments);
    if (result.unmatchedSample.length) console.log('[anitube] приклади без збігу зі статусом:', result.unmatchedSample);
    const parts: string[] = [];
    if (result.imported) parts.push(`перенесено ${result.imported} твор${result.imported === 1 ? '' : 'ів'}`);
    if (result.changed) parts.push(`змінено категорію в ${result.changed} твор${result.changed === 1 ? 'і' : 'ах'}`);
    if (result.removed) parts.push(`видалено ${result.removed} твор${result.removed === 1 ? '' : 'ів'} (зникли з усіх статусів на сайті)`);
    const totalEntries = result.segments.reduce((sum, s) => sum + s.entries, 0);
    if (!totalEntries) return 'Синхронізовано, але на anitube не знайдено жодного твору в списках (перевірте консоль)';
    return parts.length ? `Синхронізовано: ${parts.join(', ')}` : 'Синхронізовано, змін немає';
  }

  function paint(status: AnitubeStatus) {
    if (status.loggedIn) {
      connectBtn.style.display = 'none';
      loginPanel.style.display = 'none';
      accountPanel.style.display = 'block';
      // Без "оновлюється кожні ~20 хв" — деталь реалізації, яка нічого не
      // дає користувачу тут, лише плутає.
      accountSub.textContent = `${status.username} · остання синхронізація: ${fmtSyncTime(status.lastSyncAt)}`;
    } else {
      connectBtn.style.display = 'flex';
      accountPanel.style.display = 'none';
      connectLabel.textContent = 'Підключити акаунт anitube';
    }
  }

  api
    .anitubeStatus()
    .then(paint)
    .catch(() => {
      /* не критично для решти сторінки */
    });

  connectBtn.addEventListener('click', () => {
    const isOpen = loginPanel.style.display !== 'none';
    loginPanel.style.display = isOpen ? 'none' : 'block';
  });

  loginSubmit.addEventListener('click', async () => {
    const username = usernameInput.value.trim();
    const password = passwordInput.value;
    errorEl.style.display = 'none';
    if (!username || !password) {
      errorEl.textContent = 'Введіть логін і пароль.';
      errorEl.style.display = 'block';
      return;
    }
    loginSubmit.disabled = true;
    loginSubmit.textContent = 'Входжу…';
    try {
      const status = await api.anitubeLogin(username, password);
      passwordInput.value = '';
      paint(status);
      toast('Anitube підключено, синхронізую категорії…');
      const result = await api.anitubeSync();
      toast(fmtSyncResult(result));
      const fresh = await api.anitubeStatus();
      paint(fresh);
    } catch (err) {
      errorEl.textContent = (err as Error).message;
      errorEl.style.display = 'block';
    } finally {
      loginSubmit.disabled = false;
      loginSubmit.textContent = 'Увійти й синхронізувати';
    }
  });

  syncBtn.addEventListener('click', async () => {
    if (syncBtn.classList.contains('disabled-row')) return;
    syncBtn.classList.add('disabled-row');
    syncLabel.textContent = 'Синхронізую…';
    try {
      const result = await api.anitubeSync();
      toast(fmtSyncResult(result));
      const fresh = await api.anitubeStatus();
      paint(fresh);
    } catch (err) {
      toast((err as Error).message);
    } finally {
      syncBtn.classList.remove('disabled-row');
      syncLabel.textContent = 'Синхронізувати категорії аніме';
    }
  });

  disconnectBtn.addEventListener('click', async () => {
    const confirmed = await confirmDialog({
      title: 'Відключити anitube?',
      message: 'Категорії, вже проставлені в бібліотеці, залишаться як є — просто перестануть оновлюватись з акаунтом.',
      confirmText: 'Відключити',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await api.anitubeLogout();
      paint({ loggedIn: false });
      usernameInput.value = '';
    } catch (err) {
      toast((err as Error).message);
    }
  });
}
